import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  ListGroup,
  Image,
  Tabs,
  Tab,
  Table,
  Form,
  Modal,
  Button,
  Badge,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { getMatchHandicap } from "../utils/handicap";
import { getYouTubeEmbedUrl } from "../utils/youtube";
import { getLevelChipClass, getLevelTextStyle } from "../utils/levelColors";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

// ========== CONSTANTS ==========
const LEVEL_MAP = {
  8: "H", 7: "G", 1: "F", 2: "E", 3: "D", 4: "C", 5: "B", 6: "A",
};

const LEVEL_ORDER = { H: 0, G: 1, F: 2, E: 3, D: 4, C: 5, B: 6, A: 7 };

// Modern gradient colors
const CHART_COLORS = {
  primary: "rgba(59, 130, 246, 0.8)",   // Blue-500 (Accent)
  primaryBorder: "rgb(59, 130, 246)",
  success: "rgba(34, 197, 94, 0.8)",    // Green-500
  successBorder: "rgb(34, 197, 94)",
  danger: "rgba(239, 68, 68, 0.8)",     // Red-500
  dangerBorder: "rgb(239, 68, 68)",
  info: "rgba(30, 41, 59, 0.8)",        // Slate-800 (Primary)
  infoBorder: "rgb(30, 41, 59)",
  warning: "rgba(234, 179, 8, 0.8)",    // Yellow-500
  warningBorder: "rgb(234, 179, 8)",
  purple: "rgba(168, 85, 247, 0.8)",    // Purple-500
  purpleBorder: "rgb(168, 85, 247)",
};

const GRADIENT_COLORS = [
  { bg: "rgba(59, 130, 246, 0.7)", border: "rgb(59, 130, 246)" },
  { bg: "rgba(30, 41, 59, 0.7)", border: "rgb(30, 41, 59)" },
  { bg: "rgba(34, 197, 94, 0.7)", border: "rgb(34, 197, 94)" },
  { bg: "rgba(168, 85, 247, 0.7)", border: "rgb(168, 85, 247)" },
  { bg: "rgba(234, 179, 8, 0.7)", border: "rgb(234, 179, 8)" },
];

// ========== MAIN COMPONENT ==========
const PlayerDetail = () => {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [matchHistory, setMatchHistory] = useState([]);
  const [levelHistory, setLevelHistory] = useState([]);
  const [teamHistory, setTeamHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [allPlayersLevelHistoryMap, setAllPlayersLevelHistoryMap] = useState({});

  // ===== MEMOIZED VALUES =====
  const filteredMatchHistory = useMemo(() => {
    if (!selectedYear || matchHistory.length === 0) return matchHistory;
    return matchHistory.filter(
      (match) =>
        match.play_date &&
        new Date(match.play_date).getFullYear().toString() === selectedYear
    );
  }, [matchHistory, selectedYear]);

  const performanceStats = useMemo(() => {
    if (!filteredMatchHistory || filteredMatchHistory.length === 0 || !player)
      return null;

    const playerIdNum = player.id;
    const stats = {
      monthlyStats: {},
      monthlySingleStats: {},
      monthlyDoubleStats: {},
      levelStatsSingle: {},
      levelStatsDouble: {},
      totalMatches: 0,
      totalWins: 0,
      singleMatches: 0,
      singleWins: 0,
      doubleMatches: 0,
      doubleWins: 0,
      last5Wins: 0,
    };

    filteredMatchHistory.forEach((match) => {
      const isPlayer1 = match.player1_id === playerIdNum;
      const isPlayer2 = match.player2_id === playerIdNum;
      const isPlayer3 = match.player3_id === playerIdNum;
      const isPlayer4 = match.player4_id === playerIdNum;

      if (!isPlayer1 && !isPlayer2 && !isPlayer3 && !isPlayer4) return;

      // Skip matches with null results - these can cause crashes on mobile
      if (match.math_home_result == null || match.math_guest_result == null) return;

      const isPlayerInHome = isPlayer1 || isPlayer2;
      const homeResult = Number(match.math_home_result) || 0;
      const guestResult = Number(match.math_guest_result) || 0;
      const isWin = isPlayerInHome
        ? homeResult > guestResult
        : guestResult > homeResult;

      stats.totalMatches++;
      if (isWin) stats.totalWins++;

      // Skip if play_date is invalid
      if (!match.play_date) return;

      const date = new Date(match.play_date);
      if (isNaN(date.getTime())) return;

      const month = date.toISOString().split("T")[0].substring(0, 7);
      const monthLabel = date.toLocaleDateString("vi-VN", {
        month: "2-digit",
        year: "numeric",
      });

      if (!stats.monthlyStats[month]) {
        stats.monthlyStats[month] = { wins: 0, total: 0, label: monthLabel };
      }
      stats.monthlyStats[month].total++;
      if (isWin) stats.monthlyStats[month].wins++;

      const isDouble = match.player2_id && match.player4_id;
      const targetStats = isDouble
        ? stats.monthlyDoubleStats
        : stats.monthlySingleStats;

      if (!targetStats[month]) {
        targetStats[month] = { wins: 0, total: 0, label: monthLabel };
      }
      targetStats[month].total++;
      if (isWin) targetStats[month].wins++;

      if (isDouble) {
        stats.doubleMatches++;
        if (isWin) stats.doubleWins++;
      } else {
        stats.singleMatches++;
        if (isWin) stats.singleWins++;
      }

      let opponentLevel, opponentLevel2;
      if (isPlayer1 || isPlayer2) {
        opponentLevel = match.player3_level;
        opponentLevel2 = match.player4_level;
      } else {
        opponentLevel = match.player1_level;
        opponentLevel2 = match.player2_level;
      }

      if (isDouble && opponentLevel && opponentLevel2) {
        const levels = [opponentLevel, opponentLevel2].sort(
          (a, b) => LEVEL_ORDER[LEVEL_MAP[a]] - LEVEL_ORDER[LEVEL_MAP[b]]
        );
        const key = `${levels[0]}-${levels[1]}`;
        if (!stats.levelStatsDouble[key]) {
          stats.levelStatsDouble[key] = { wins: 0, total: 0 };
        }
        stats.levelStatsDouble[key].total++;
        if (isWin) stats.levelStatsDouble[key].wins++;
      } else if (!isDouble && opponentLevel) {
        if (!stats.levelStatsSingle[opponentLevel]) {
          stats.levelStatsSingle[opponentLevel] = { wins: 0, total: 0 };
        }
        stats.levelStatsSingle[opponentLevel].total++;
        if (isWin) stats.levelStatsSingle[opponentLevel].wins++;
      }
    });

    for (let i = 0; i < Math.min(5, filteredMatchHistory.length); i++) {
      const match = filteredMatchHistory[i];
      // Skip matches with null results
      if (match.math_home_result == null || match.math_guest_result == null) continue;

      const isPlayer1 = match.player1_id === playerIdNum;
      const isPlayer2 = match.player2_id === playerIdNum;
      const homeResult = Number(match.math_home_result) || 0;
      const guestResult = Number(match.math_guest_result) || 0;
      const isWin =
        isPlayer1 || isPlayer2
          ? homeResult > guestResult
          : guestResult > homeResult;
      if (isWin) stats.last5Wins++;
    }

    // Get all unique months from all stats, sorted
    const allMonths = [...new Set([
      ...Object.keys(stats.monthlyStats),
      ...Object.keys(stats.monthlySingleStats),
      ...Object.keys(stats.monthlyDoubleStats),
    ])].sort((a, b) => a.localeCompare(b)).slice(0, 10);

    const formatMonthlyStats = (monthlyData) =>
      allMonths.map((month) => {
        const data = monthlyData[month];
        // Create a label for months that might not have data
        const date = new Date(month + "-01");
        const monthLabel = date.toLocaleDateString("vi-VN", {
          month: "2-digit",
          year: "numeric",
        });
        if (data) {
          return {
            month: data.label,
            wins: data.wins,
            losses: data.total - data.wins,
            total: data.total,
            winRate: parseFloat((data.wins / data.total * 100).toFixed(1)),
          };
        } else {
          return {
            month: monthLabel,
            wins: 0,
            losses: 0,
            total: 0,
            winRate: 0,
          };
        }
      });

    const formatLevelStats = (levelData, isSingle = true) =>
      Object.entries(levelData)
        .map(([levelId, data]) => ({
          level: isSingle
            ? `Hạng ${LEVEL_MAP[levelId] || levelId}`
            : `Hạng ${levelId
              .split("-")
              .map((l) => LEVEL_MAP[l] || l)
              .join("-")}`,
          winRate: parseFloat((data.wins / data.total * 100).toFixed(1)),
          matches: data.total,
          wins: data.wins,
          levelKey: levelId,
        }))
        .sort((a, b) => {
          const aLevels = a.levelKey
            .split("-")
            .map((l) => LEVEL_ORDER[LEVEL_MAP[l] || l] || 0);
          const bLevels = b.levelKey
            .split("-")
            .map((l) => LEVEL_ORDER[LEVEL_MAP[l] || l] || 0);
          return (
            aLevels[0] - bLevels[0] || (aLevels[1] || 0) - (bLevels[1] || 0)
          );
        });

    return {
      overview: {
        totalMatches: stats.totalMatches,
        totalWins: stats.totalWins,
        winRate:
          stats.totalMatches > 0
            ? parseFloat((stats.totalWins / stats.totalMatches * 100).toFixed(1))
            : 0,
        last5Wins: stats.last5Wins,
        last5Rate: parseFloat((stats.last5Wins / 5 * 100).toFixed(1)),
        singleMatches: stats.singleMatches,
        singleWins: stats.singleWins,
        doubleMatches: stats.doubleMatches,
        doubleWins: stats.doubleWins,
      },
      recentForm: formatMonthlyStats(stats.monthlyStats),
      recentFormSingle: formatMonthlyStats(stats.monthlySingleStats),
      recentFormDouble: formatMonthlyStats(stats.monthlyDoubleStats),
      winRateVsLevelsSingle: formatLevelStats(stats.levelStatsSingle, true),
      winRateVsLevelsDouble: formatLevelStats(stats.levelStatsDouble, false),
    };
  }, [filteredMatchHistory, player]);

  // ===== CALLBACK FUNCTIONS =====
  const getLevelNameFromId = useCallback(
    (levelId) => LEVEL_MAP[levelId] || `ID ${levelId}`,
    []
  );

  const getPlayerLevelAtDate = useCallback(
    (playerLevelHistory, matchDate, currentLevelId) => {
      const matchTimestamp = new Date(matchDate).getTime();
      const sortedHistory = [...playerLevelHistory].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      if (sortedHistory.length === 0) return currentLevelId;

      let historicalLevelId = currentLevelId;
      for (const levelChange of sortedHistory) {
        const changeTimestamp = new Date(levelChange.created_at).getTime();
        if (changeTimestamp <= matchTimestamp) {
          historicalLevelId = levelChange.level_new;
        } else {
          break;
        }
      }

      if (
        historicalLevelId === currentLevelId &&
        sortedHistory.length > 0 &&
        new Date(sortedHistory[0].created_at).getTime() > matchTimestamp
      ) {
        historicalLevelId = sortedHistory[0].level_old;
      }

      return historicalLevelId;
    },
    []
  );

  // ===== DATA FETCHING =====
  useEffect(() => {
    const fetchPlayerDetails = async () => {
      try {
        console.log('[PlayerDetail] Fetching player data for ID:', id);

        const responses = await Promise.all([
          axios.get(`${API_URL}/playerspublic/${id}`),
          axios.get(`${API_URL}/playerspublic/${id}/match-history`),
          axios.get(`${API_URL}/playerspublic/${id}/level-history`),
          axios.get(`${API_URL}/playerspublic/${id}/team-history`),
        ]);

        const [playerRes, matchHistoryRes, levelHistoryRes, teamHistoryRes] =
          responses;

        console.log('[PlayerDetail] Player data received:', {
          player: playerRes.data?.name,
          matchCount: matchHistoryRes.data?.length,
          levelHistoryCount: levelHistoryRes.data?.length,
          teamHistoryCount: teamHistoryRes.data?.length,
        });

        // Validate data before setting state
        if (!playerRes.data) {
          throw new Error('Player data is null or undefined');
        }

        setPlayer(playerRes.data);
        setMatchHistory(Array.isArray(matchHistoryRes.data) ? matchHistoryRes.data : []);
        setLevelHistory(Array.isArray(levelHistoryRes.data) ? levelHistoryRes.data : []);
        setTeamHistory(Array.isArray(teamHistoryRes.data) ? teamHistoryRes.data : []);

        const uniquePlayerIds = new Set();
        (matchHistoryRes.data || []).forEach((match) => {
          [match.player1_id, match.player2_id, match.player3_id, match.player4_id].forEach(
            (pid) => {
              if (pid && pid !== id) uniquePlayerIds.add(pid);
            }
          );
        });

        const levelHistories = await Promise.all(
          Array.from(uniquePlayerIds).map((pid) =>
            axios
              .get(`${API_URL}/playerspublic/${pid}/level-history`)
              .then((res) => ({ playerId: pid, history: res.data }))
              .catch(() => ({ playerId: pid, history: [] }))
          )
        );

        const historyMap = levelHistories.reduce((acc, { playerId, history }) => {
          acc[playerId] = history;
          return acc;
        }, {});

        setAllPlayersLevelHistoryMap(historyMap);
      } catch (err) {
        const errorInfo = {
          message: err.message,
          stack: err.stack,
          playerId: id,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        };
        console.error("[PlayerDetail] Error fetching player details:", errorInfo);

        // Save error to localStorage for debugging on mobile
        try {
          const errors = JSON.parse(localStorage.getItem('playerDetailErrors') || '[]');
          errors.unshift(errorInfo);
          localStorage.setItem('playerDetailErrors', JSON.stringify(errors.slice(0, 10)));
        } catch (e) {
          console.error('[PlayerDetail] Could not save error to localStorage');
        }

        setError(`Failed to load player details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayerDetails();
  }, [id]);

  useEffect(() => {
    if (matchHistory.length > 0 && !selectedYear) {
      const years = [
        ...new Set(
          matchHistory
            .map((match) => {
              if (!match.play_date) return null;
              const date = new Date(match.play_date);
              const year = date.getFullYear();
              return isNaN(year) ? null : year;
            })
            .filter((year) => year !== null)
        ),
      ].sort((a, b) => b - a);
      if (years.length > 0) setSelectedYear(years[0].toString());
    }
  }, [matchHistory, selectedYear]);

  // ===== LOADING & ERROR STATES =====
  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </Spinner>
        <p className="mt-3">Đang tải thông tin vận động viên...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert
          variant="danger"
          className="border-0 shadow-sm"
          style={{ borderRadius: "12px" }}
        >
          Không thể tải chi tiết vận động viên.
        </Alert>
      </Container>
    );
  }

  if (!player) {
    return (
      <Container className="mt-5">
        <Alert
          variant="info"
          className="border-0 shadow-sm"
          style={{ borderRadius: "12px" }}
        >
          Không tìm thấy vận động viên.
        </Alert>
      </Container>
    );
  }

  const currentLevel = getLevelNameFromId(player.level_present);
  const levelPoint = parseFloat(player.level_point) || 0;
  const playerName = player.name || 'Unknown';
  const yearsOptions = [
    ...new Set(
      matchHistory
        .filter((match) => match.play_date && !isNaN(new Date(match.play_date).getTime()))
        .map((match) => new Date(match.play_date).getFullYear())
    ),
  ].sort((a, b) => b - a);

  return (
    <Container className="py-4" style={{ maxWidth: "1400px" }}>
      <div className="mb-4">
        <h1 className="fw-bold mb-1">Hồ Sơ Vận Động Viên</h1>
        <p className="text-muted small">Chi tiết thông tin và phong độ thi đấu của {playerName}</p>
      </div>

      {/* Player Info Card */}
      <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={3} xs={12} className="text-center mb-3 mb-md-0">
              {player.image ? (
                <Image
                  src={`${S3_BASE_URL}/player/${player.image}`}
                  roundedCircle
                  style={{
                    width: "180px",
                    height: "180px",
                    objectFit: "cover",
                    border: "4px solid #fff",
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "180px",
                    height: "180px",
                    borderRadius: "50%",
                    backgroundColor: "#f8f9fa",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "4rem",
                    color: "var(--primary-color)",
                    border: "4px solid #fff",
                    fontWeight: 600,
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {playerName.charAt(0)}
                </div>
              )}
            </Col>
            <Col md={9} xs={12}>
              <h2 className="mb-4 fw-bold">
                {playerName}
              </h2>
              <Row>
                <Col md={6} xs={12}>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center border-0">
                      <span className="text-muted small text-uppercase fw-bold">Ngày Sinh</span>
                      <span className="fw-semibold">
                        {player.date_of_birth}
                      </span>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center border-0">
                      <span className="text-muted small text-uppercase fw-bold">Hạng Hiện Tại</span>
                      <span
                        className="level-text"
                        style={{ fontSize: "1.1rem", ...getLevelTextStyle(currentLevel) }}
                      >
                        Hạng {currentLevel}
                      </span>
                    </ListGroup.Item>
                  </ListGroup>
                </Col>
                <Col md={6} xs={12}>
                  <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center border-0">
                      <span className="text-muted small text-uppercase fw-bold">Điểm Tích Lũy</span>
                      <span
                        className={`fw-bold fs-5 text-${levelPoint >= 100
                          ? "success"
                          : levelPoint <= -100
                            ? "danger"
                            : levelPoint >= 50
                              ? "warning"
                              : levelPoint <= -50
                                ? "warning"
                                : "dark"
                          }`}
                      >
                        {levelPoint >= 100 && "↑ "}
                        {levelPoint <= -100 && "↓ "}
                        {levelPoint.toFixed(2)}
                      </span>
                    </ListGroup.Item>
                  </ListGroup>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Stats Cards */}
      <Row className="mb-4 g-3">
        <Col md={4} xs={12}>
          <Card className="text-center border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <Card.Body className="py-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Trận Đấu</p>
              <h2 className="fw-bold mb-0">
                {matchHistory.length}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} xs={12}>
          <Card className="text-center border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <Card.Body className="py-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Thăng/Hạ Hạng</p>
              <h2 className="fw-bold mb-0">
                {levelHistory.length}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} xs={12}>
          <Card className="text-center border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <Card.Body className="py-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">CLB Tham Gia</p>
              <h2 className="fw-bold mb-0">
                {teamHistory.length}
              </h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Tabs */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", overflow: "hidden" }}>
        <Tabs
          defaultActiveKey="matchHistory"
          id="player-details-tabs"
          className="custom-tabs-minimal"
        >
          <Tab
            eventKey="matchHistory"
            title="Lịch Sử Thi Đấu"
            tabClassName="fw-semibold"
          >
            <MatchHistoryTab
              filteredMatchHistory={filteredMatchHistory}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              yearsOptions={yearsOptions}
              player={player}
              levelHistory={levelHistory}
              allPlayersLevelHistoryMap={allPlayersLevelHistoryMap}
              getLevelNameFromId={getLevelNameFromId}
              getPlayerLevelAtDate={getPlayerLevelAtDate}
              setShowVideoModal={setShowVideoModal}
              setCurrentVideoUrl={setCurrentVideoUrl}
              />
          </Tab>

          <Tab
            eventKey="levelHistory"
            title="Lịch Sử Đổi Hạng"
            tabClassName="fw-semibold"
          >
            <LevelHistoryTab
              levelHistory={levelHistory}
              getLevelNameFromId={getLevelNameFromId}
            />
          </Tab>

          <Tab eventKey="teamHistory" title="Lịch Sử Đội" tabClassName="fw-semibold">
            <TeamHistoryTab teamHistory={teamHistory} />
          </Tab>

          <Tab
            eventKey="performance"
            title="Phong Độ"
            tabClassName="fw-semibold"
          >
            <PerformanceTab
              performanceStats={performanceStats}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              yearsOptions={yearsOptions}
            />
          </Tab>

          <Tab
            eventKey="winRateByLevel"
            title="Tỷ Lệ vs Hạng"
            tabClassName="fw-semibold"
          >
            <WinRateByLevelTab
              performanceStats={performanceStats}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              yearsOptions={yearsOptions}
            />
          </Tab>
        </Tabs>
      </Card>

      {/* Video Modal */}
      <Modal
        show={showVideoModal}
        onHide={() => {
          setShowVideoModal(false);
          setCurrentVideoUrl("");
        }}
        size="xl"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "var(--primary-color)",
            color: "white",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Modal.Title className="fw-bold">Video Trận Đấu</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ borderRadius: "0 0 12px 12px" }}>
          {(() => {
            if (!currentVideoUrl) {
              return <div className="text-center py-5"><p>Không có video để hiển thị.</p></div>;
            }
            const embedUrl = getYouTubeEmbedUrl(currentVideoUrl);
            if (embedUrl) {
              return (
                <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%", height: 0, backgroundColor: "#000" }}>
                  <iframe
                    src={embedUrl}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Match Video"
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
                  />
                </div>
              );
            }
            return (
              <div className="text-center py-4">
                <p className="text-muted">Link video không hỗ trợ xem trực tiếp</p>
                <a href={currentVideoUrl} target="_blank" rel="noopener noreferrer" className="d-block mb-3">{currentVideoUrl}</a>
                <Button variant="primary" onClick={() => window.open(currentVideoUrl, '_blank')}>Mở trong tab mới</Button>
              </div>
            );
          })()}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

// ========== SUB COMPONENTS ==========

const MatchHistoryTab = ({
  filteredMatchHistory,
  selectedYear,
  setSelectedYear,
  yearsOptions,
  player,
  levelHistory,
  allPlayersLevelHistoryMap,
  getLevelNameFromId,
  getPlayerLevelAtDate,
  setShowVideoModal,
  setCurrentVideoUrl,
}) => (
  <div className="p-4">
    <Form.Group controlId="selectYearMatchHistory" className="mb-4">
      <Row>
        <Col md={4}>
          <Form.Label className="fw-bold text-primary">
            Chọn Năm
          </Form.Label>
          <Form.Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            size="lg"
            style={{ borderRadius: "8px" }}
          >
            <option value="">Tất cả các năm</option>
            {yearsOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={8} className="d-flex align-items-end">
          <Alert variant="info" className="mb-0 w-100 border-0" style={{ borderRadius: "8px" }}>
            <small>
              Tìm thấy <strong>{filteredMatchHistory.length}</strong> trận đấu
              {selectedYear && ` trong năm ${selectedYear}`}
            </small>
          </Alert>
        </Col>
      </Row>
    </Form.Group>

    {filteredMatchHistory.length > 0 ? (
      <div className="table-responsive" style={{ borderRadius: "8px", overflowX: "auto" }}>
        <Table hover className="mb-0" style={{ minWidth: "800px" }}>
          <thead style={{ backgroundColor: "var(--primary-color)", color: "white" }}>
            <tr>
              <th className="text-center" style={{ width: "50px" }}>
                #
              </th>
              <th style={{ width: "100px" }}>Ngày</th>
              <th>Đội Nhà</th>
              <th className="text-center" style={{ width: "100px" }}>
                Tỷ Số
              </th>
              <th>Đội Khách</th>
              <th className="text-center" style={{ width: "120px" }}>
                Điểm
              </th>
              <th className="text-center" style={{ width: "80px" }}>
                Video
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredMatchHistory.map((match, index) => (
              <MatchRow
                key={`match-${match.math_id || index}-${index}`}
                match={match}
                index={index}
                player={player}
                levelHistory={levelHistory}
                allPlayersLevelHistoryMap={allPlayersLevelHistoryMap}
                getLevelNameFromId={getLevelNameFromId}
                getPlayerLevelAtDate={getPlayerLevelAtDate}
                setShowVideoModal={setShowVideoModal}
                setCurrentVideoUrl={setCurrentVideoUrl}
              />
            ))}
          </tbody>
        </Table>
      </div>
    ) : (
      <Alert variant="info" className="mb-0 border-0" style={{ borderRadius: "12px" }}>
        Không tìm thấy lịch sử thi đấu{selectedYear && ` cho năm ${selectedYear}`}.
      </Alert>
    )}
  </div>
);

const MatchRow = ({
  match,
  index,
  player,
  levelHistory,
  allPlayersLevelHistoryMap,
  getLevelNameFromId,
  getPlayerLevelAtDate,
  setShowVideoModal,
  setCurrentVideoUrl,
}) => {
  const currentPlayerId = player.id;
  const isPlayer1 = match.player1_id === currentPlayerId;
  const isPlayer2 = match.player2_id === currentPlayerId;
  const isPlayer3 = match.player3_id === currentPlayerId;
  const isPlayer4 = match.player4_id === currentPlayerId;

  const isPlayerInHome = isPlayer1 || isPlayer2;

  // Safe comparison with null check and type conversion
  const homeResult = Number(match.math_home_result) || 0;
  const guestResult = Number(match.math_guest_result) || 0;
  const hasValidResult = match.math_home_result != null && match.math_guest_result != null;
  const isWin = hasValidResult && (
    isPlayerInHome
      ? homeResult > guestResult
      : guestResult > homeResult
  );

  const playerPoint = isPlayerInHome ? match.home_point : match.guest_point;

  const player1HistoricalLevelId = isPlayer1
    ? getPlayerLevelAtDate(levelHistory, match.play_date, player.level_present)
    : getPlayerLevelAtDate(
      allPlayersLevelHistoryMap[match.player1_id] || [],
      match.play_date,
      match.player1_level
    );

  const player2HistoricalLevelId = isPlayer2
    ? getPlayerLevelAtDate(levelHistory, match.play_date, player.level_present)
    : getPlayerLevelAtDate(
      allPlayersLevelHistoryMap[match.player2_id] || [],
      match.play_date,
      match.player2_level
    );

  const player3HistoricalLevelId = isPlayer3
    ? getPlayerLevelAtDate(levelHistory, match.play_date, player.level_present)
    : getPlayerLevelAtDate(
      allPlayersLevelHistoryMap[match.player3_id] || [],
      match.play_date,
      match.player3_level
    );

  const player4HistoricalLevelId = isPlayer4
    ? getPlayerLevelAtDate(levelHistory, match.play_date, player.level_present)
    : getPlayerLevelAtDate(
      allPlayersLevelHistoryMap[match.player4_id] || [],
      match.play_date,
      match.player4_level
    );

  const handicap = getMatchHandicap(
    [getLevelNameFromId(player1HistoricalLevelId), getLevelNameFromId(player2HistoricalLevelId)].filter(Boolean),
    [getLevelNameFromId(player3HistoricalLevelId), getLevelNameFromId(player4HistoricalLevelId)].filter(Boolean)
  );

  return (
    <tr className="align-middle">
      <td className="text-center fw-bold text-muted">{index + 1}</td>
      <td>
        <small>
          {(() => {
            try {
              const date = new Date(match.play_date);
              return isNaN(date.getTime()) ? match.play_date : date.toLocaleDateString("vi-VN");
            } catch (e) {
              return match.play_date || '-';
            }
          })()}
        </small>
      </td>
      <td>
        <PlayerTeamDisplay
          player1Id={match.player1_id}
          player1Name={match.player1_name}
          player1LevelId={player1HistoricalLevelId}
          player1Point={match.point_player_1}
          player2Id={match.player2_id}
          player2Name={match.player2_name}
          player2LevelId={player2HistoricalLevelId}
          player2Point={match.point_player_2}
          isPlayerInTeam={isPlayerInHome}
          handicap={handicap?.side === 'home' ? handicap.value : null}
          getLevelNameFromId={getLevelNameFromId}
        />
      </td>
      <td className="text-center">
        <Badge
          bg={hasValidResult ? (isWin ? "success" : "danger") : "secondary"}
          className="fs-6 px-3"
          style={{ borderRadius: "8px" }}
        >
          {match.math_home_result ?? '-'} - {match.math_guest_result ?? '-'}
        </Badge>
      </td>
      <td>
        <PlayerTeamDisplay
          player1Id={match.player3_id}
          player1Name={match.player3_name}
          player1LevelId={player3HistoricalLevelId}
          player1Point={match.point_player_3}
          player2Id={match.player4_id}
          player2Name={match.player4_name}
          player2LevelId={player4HistoricalLevelId}
          player2Point={match.point_player_4}
          isPlayerInTeam={!isPlayerInHome}
          handicap={handicap?.side === 'guest' ? handicap.value : null}
          getLevelNameFromId={getLevelNameFromId}
        />
      </td>
      <td className="text-center">
        <Badge
          bg={playerPoint == null ? "secondary" : (parseFloat(playerPoint) >= 0 ? "success" : "danger")}
          className="fs-6 px-3"
          style={{ borderRadius: "8px" }}
        >
          {playerPoint == null ? '-' : (parseFloat(playerPoint) >= 0 ? "+" : "") + parseFloat(playerPoint).toFixed(2)}
        </Badge>
      </td>
      <td className="text-center">
        {match.video &&
          (match.video.startsWith("http://") || match.video.startsWith("https://")) ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setCurrentVideoUrl(match.video);
              setShowVideoModal(true);
            }}
            style={{ borderRadius: "6px" }}
          >
            ▶️
          </Button>
        ) : (
          <small className="text-muted">-</small>
        )}
      </td>
    </tr>
  );
};

const PlayerTeamDisplay = ({
  player1Id,
  player1Name,
  player1LevelId,
  player1Point,
  player2Id,
  player2Name,
  player2LevelId,
  player2Point,
  isPlayerInTeam,
  handicap,
  getLevelNameFromId,
}) => {
  const renderPlayer = (playerId, playerName, levelId, playerPoint) => {
    const levelName = getLevelNameFromId(levelId);
    const pointValue = parseFloat(playerPoint);
    const hasPoint = !isNaN(pointValue);

    return (
      <div style={{ color: isPlayerInTeam ? "#e67e22" : "inherit" }}>
        <span>
          {playerId ? (
            <Link to={`/players/${playerId}`} style={{ textDecoration: "none", color: "inherit" }}>
              {playerName}
            </Link>
          ) : (
            playerName
          )}
        </span>
        <span className={`${getLevelChipClass(levelName)} ms-2`}>
          {levelName}
        </span>
        {hasPoint && (
          <span
            className="ms-1"
            style={{
              fontSize: "0.75rem",
              color: "#6c757d",
              fontWeight: "500"
            }}
            title="Điểm tích lũy trước trận đấu"
          >
            ({pointValue.toFixed(1)})
          </span>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex align-items-start gap-1">
        <div>
          {renderPlayer(player1Id, player1Name, player1LevelId, player1Point)}
        </div>
        {handicap && (
          <Badge bg="warning" text="dark" className="fw-semibold" style={{ fontSize: "0.68rem", borderRadius: "5px" }} title="Chấp theo hạng">
            {handicap}
          </Badge>
        )}
      </div>
      {player2Name && (
        <div className="small text-muted">
          {renderPlayer(player2Id, player2Name, player2LevelId, player2Point)}
        </div>
      )}
    </div>
  );
};

const LevelHistoryTab = ({ levelHistory, getLevelNameFromId }) => (
  <div className="p-4">
    {levelHistory.length > 0 ? (
      <ListGroup>
        {levelHistory.map((history, index) => {
          const oldLevel = getLevelNameFromId(history.level_old);
          const newLevel = getLevelNameFromId(history.level_new);
          const isUpgrade = LEVEL_ORDER[newLevel] > LEVEL_ORDER[oldLevel];

          return (
            <ListGroup.Item
              key={index}
              className="d-flex justify-content-between align-items-center border"
              style={{ borderRadius: "8px", marginBottom: "8px" }}
            >
              <div>
                <div className="mb-2">
                  <small className="text-muted">
                    {new Date(history.created_at).toLocaleDateString("vi-VN")}
                  </small>
                </div>
                <div>
                  <span className={`${getLevelChipClass(oldLevel)} level-chip-lg me-2`}>
                    Hạng {oldLevel}
                  </span>
                  <span className="mx-2 fw-bold" style={{ color: "#e67e22" }}>
                    ➜
                  </span>
                  <span className={`${getLevelChipClass(newLevel)} level-chip-lg`}>
                    Hạng {newLevel}
                  </span>
                </div>
              </div>
              <Badge
                bg={isUpgrade ? "success" : "danger"}
                className="fs-4 px-3 py-2"
                style={{ borderRadius: "8px" }}
              >
                {isUpgrade ? "↑" : "↓"}
              </Badge>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    ) : (
      <Alert variant="info" className="mb-0 border-0" style={{ borderRadius: "12px" }}>
        Chưa có lịch sử thay đổi cấp bậc.
      </Alert>
    )}
  </div>
);

const TeamHistoryTab = ({ teamHistory }) => (
  <div className="p-4">
    {teamHistory.length > 0 ? (
      <ListGroup>
        {teamHistory.map((history, index) => (
          <ListGroup.Item
            key={index}
            className="border"
            style={{ borderRadius: "8px", marginBottom: "8px" }}
          >
            <Row>
              <Col md={8}>
                <h6 className="mb-2 fw-bold" style={{ color: "#e67e22" }}>
                  {history.team_name || "Không có tên đội"}
                </h6>
                <div className="mb-2">
                  <Badge bg="primary" className="me-2 px-3 py-2" style={{ borderRadius: "8px" }}>
                    {history.season_name || "N/A"}
                  </Badge>
                  <Badge
                    bg={history.team_role === 1 ? "info" : "warning"}
                    className="px-3 py-2"
                    style={{ borderRadius: "8px" }}
                  >
                    {history.team_role === 1 ? "Vận Động Viên" : "Đội Trưởng"}
                  </Badge>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <small className="text-muted">
                  Tham gia: {new Date(history.created_at).toLocaleDateString("vi-VN")}
                </small>
              </Col>
            </Row>
          </ListGroup.Item>
        ))}
      </ListGroup>
    ) : (
      <Alert variant="info" className="mb-0 border-0" style={{ borderRadius: "12px" }}>
        Chưa có lịch sử tham gia đội.
      </Alert>
    )}
  </div>
);

const PerformanceTab = ({
  performanceStats,
  selectedYear,
  setSelectedYear,
  yearsOptions,
}) => {
  if (!performanceStats) {
    return (
      <div className="p-4">
        <Alert variant="info" className="border-0" style={{ borderRadius: "12px" }}>
          Không có dữ liệu phong độ.
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Form.Group controlId="selectYearPerformance" className="mb-4">
        <Row>
          <Col md={4}>
            <Form.Label className="fw-bold" style={{ color: "#e67e22" }}>
              Chọn Năm
            </Form.Label>
            <Form.Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              size="lg"
              style={{ borderRadius: "8px" }}
            >
              <option value="">Tất cả các năm</option>
              {yearsOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </Form.Group>

      {/* KPIs */}
      <Row className="mb-4 g-3">
        {[
          {
            label: "Tổng Trận",
            value: performanceStats.overview.totalMatches,
            color: "#e67e22",
          },
          {
            label: "Số Trận Thắng",
            value: performanceStats.overview.totalWins,
            color: "#27ae60",
          },
          {
            label: "Tỷ Lệ Thắng",
            value: `${performanceStats.overview.winRate}%`,
            color: "#3498db",
          },
          {
            label: "5 Trận Gần (W)",
            value: `${performanceStats.overview.last5Wins}/5`,
            color: "#9b59b6",
          },
          {
            label: "Tổng Trận Đơn",
            value: performanceStats.overview.singleMatches,
            color: "#e67e22",
          },
          {
            label: "Thắng Trận Đơn",
            value: performanceStats.overview.singleWins,
            color: "#27ae60",
          },
          {
            label: "Tổng Trận Đôi",
            value: performanceStats.overview.doubleMatches,
            color: "#e67e22",
          },
          {
            label: "Thắng Trận Đôi",
            value: performanceStats.overview.doubleWins,
            color: "#27ae60",
          },
        ].map((stat, idx) => (
          <Col md={3} key={idx}>
            <Card className="text-center border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
              <Card.Body className="py-3">
                <h6 className="text-muted mb-2">{stat.label}</h6>
                <h3 className="fw-bold mb-0" style={{ color: stat.color }}>
                  {stat.value}
                </h3>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Line Chart - Win Rate Trend */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-4" style={{ color: "#e67e22" }}>
                Xu Hướng Tỷ Lệ Thắng
              </h5>
              <div style={{ height: "300px" }}>
                <Line
                  data={{
                    labels: performanceStats.recentForm.map((item) => item.month),
                    datasets: [
                      {
                        label: "Tỷ Lệ Thắng %",
                        data: performanceStats.recentForm.map((item) => item.winRate),
                        borderColor: CHART_COLORS.primaryBorder,
                        backgroundColor: "rgba(230, 126, 34, 0.1)",
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointRadius: 6,
                        pointBackgroundColor: CHART_COLORS.primaryBorder,
                        pointBorderColor: "#fff",
                        pointBorderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: { callback: (value) => `${value}%` },
                      },
                      x: { grid: { display: false } },
                    },
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Doughnut Chart - Win/Loss Distribution */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-4" style={{ color: "#e67e22" }}>
                Thống Kê Thắng/Thua
              </h5>
              <div style={{ height: "300px" }}>
                <Doughnut
                  data={{
                    labels: ["Thắng", "Thua"],
                    datasets: [
                      {
                        data: [
                          performanceStats.overview.totalWins,
                          performanceStats.overview.totalMatches - performanceStats.overview.totalWins,
                        ],
                        backgroundColor: [CHART_COLORS.success, CHART_COLORS.danger],
                        borderColor: [CHART_COLORS.successBorder, CHART_COLORS.dangerBorder],
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom", labels: { font: { size: 14 }, padding: 15 } },
                    },
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Stacked Bar Chart for Single/Double */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "12px" }}>
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-4" style={{ color: "#e67e22" }}>
            So Sánh Trận Đơn & Đôi
          </h5>
          <div style={{ height: "300px" }}>
            <Bar
              data={{
                labels: performanceStats.recentForm.map((item) => item.month),
                datasets: [
                  {
                    label: "Đơn - Thắng",
                    data: performanceStats.recentFormSingle.map((item) => item.wins),
                    backgroundColor: CHART_COLORS.success,
                    borderRadius: 6,
                    borderSkipped: false,
                  },
                  {
                    label: "Đôi - Thắng",
                    data: performanceStats.recentFormDouble.map((item) => item.wins),
                    backgroundColor: CHART_COLORS.purple,
                    borderRadius: 6,
                    borderSkipped: false,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: "top" },
                },
                scales: {
                  x: { grid: { display: false } },
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Detail Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-4" style={{ color: "#e67e22" }}>
            Chi Tiết Hàng Tháng
          </h5>
          <div className="table-responsive" style={{ borderRadius: "8px" }}>
            <Table hover className="mb-0">
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th>Tháng</th>
                  <th className="text-center">Thắng</th>
                  <th className="text-center">Thua</th>
                  <th className="text-center">Tổng</th>
                  <th className="text-right">Tỷ Lệ</th>
                </tr>
              </thead>
              <tbody>
                {performanceStats.recentForm.map((m, idx) => (
                  <tr key={idx}>
                    <td className="fw-semibold">{m.month}</td>
                    <td className="text-center">
                      <Badge bg="success" className="px-3 py-2" style={{ borderRadius: "6px" }}>
                        {m.wins}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="danger" className="px-3 py-2" style={{ borderRadius: "6px" }}>
                        {m.losses}
                      </Badge>
                    </td>
                    <td className="text-center">{m.total}</td>
                    <td className="text-right">
                      <Badge
                        bg={
                          m.winRate >= 70
                            ? "success"
                            : m.winRate >= 50
                              ? "info"
                              : m.winRate >= 30
                                ? "warning"
                                : "danger"
                        }
                        className="px-3 py-2"
                        style={{ borderRadius: "6px" }}
                      >
                        {m.winRate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

const WinRateByLevelTab = ({
  performanceStats,
  selectedYear,
  setSelectedYear,
  yearsOptions,
}) => {
  if (!performanceStats) {
    return (
      <div className="p-4">
        <Alert variant="info" className="border-0" style={{ borderRadius: "12px" }}>
          Không có dữ liệu tỷ lệ thắng.
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Form.Group controlId="selectYearWinRate" className="mb-4">
        <Row>
          <Col md={4}>
            <Form.Label className="fw-bold" style={{ color: "#e67e22" }}>
              Chọn Năm
            </Form.Label>
            <Form.Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              size="lg"
              style={{ borderRadius: "8px" }}
            >
              <option value="">Tất cả các năm</option>
              {yearsOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </Form.Group>

      <Tabs
        defaultActiveKey="singleWinRate"
        id="win-rate-type-tabs"
        className="mb-3 border-bottom-0"
        style={{ backgroundColor: "#f8f9fa", padding: "10px 10px 0 10px" }}
      >
        <Tab eventKey="singleWinRate" title="Đơn" tabClassName="fw-semibold">
          <WinRateChart
            data={performanceStats.winRateVsLevelsSingle}
            title="Tỷ Lệ Thắng Đơn Theo Hạng Đối Thủ"
            type="single"
          />
        </Tab>
        <Tab eventKey="doubleWinRate" title="Đôi" tabClassName="fw-semibold">
          <WinRateChart
            data={performanceStats.winRateVsLevelsDouble}
            title="Tỷ Lệ Thắng Đôi Theo Hạng Đối Thủ"
            type="double"
          />
        </Tab>
      </Tabs>
    </div>
  );
};

const WinRateChart = ({ data, title, type }) => {
  if (!data || data.length === 0) {
    return (
      <div className="p-4">
        <Alert variant="info" className="border-0" style={{ borderRadius: "12px" }}>
          Không có dữ liệu.
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-4" style={{ color: "#e67e22" }}>
                {title}
              </h5>
              <div style={{ height: "350px" }}>
                <Bar
                  data={{
                    labels: data.map((item) => item.level),
                    datasets: [
                      {
                        label: "Tỷ Lệ Thắng %",
                        data: data.map((item) => item.winRate),
                        backgroundColor: data.map((item, idx) => GRADIENT_COLORS[idx % GRADIENT_COLORS.length].bg),
                        borderColor: data.map((item, idx) => GRADIENT_COLORS[idx % GRADIENT_COLORS.length].border),
                        borderWidth: 2,
                        borderRadius: 8,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: { callback: (value) => `${value}%` },
                      },
                      x: { grid: { display: false } },
                    },
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-4" style={{ color: "#e67e22" }}>
            Thống Kê Chi Tiết
          </h5>
          <div className="table-responsive" style={{ borderRadius: "8px" }}>
            <Table hover className="mb-0">
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th>Hạng Đối Thủ</th>
                  <th className="text-center">Thắng</th>
                  <th className="text-center">Tổng</th>
                  <th className="text-right">Tỷ Lệ</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, idx) => {
                  const badgeColor =
                    item.winRate >= 70
                      ? "success"
                      : item.winRate >= 50
                        ? "info"
                        : item.winRate >= 30
                          ? "warning"
                          : "danger";

                  return (
                    <tr key={idx}>
                      <td>
                        <span className={getLevelChipClass(item.level)}>{item.level}</span>
                      </td>
                      <td className="text-center">
                        <Badge bg="light" text="dark" className="px-3 py-2" style={{ borderRadius: "6px" }}>
                          {item.wins}
                        </Badge>
                      </td>
                      <td className="text-center">{item.matches}</td>
                      <td className="text-right">
                        <Badge bg={badgeColor} className="px-3 py-2" style={{ borderRadius: "6px" }}>
                          {item.winRate}%
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PlayerDetail;
